import React, { useEffect, useRef, useCallback, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import {
  Card,
  CardHeader,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Collapse,
  IconButton,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import queryString from 'query-string'
import useAxios from '../../../../../hooks/useAxios'
import { numberFormat } from '../../../../../utils'
import PageUrls from '../../../../../constants/page-urls'

interface ProductPopularData {
  _id: string
  productName: string
  count: number
  total: number
  quantity: number
}

interface ProductPopularProps {
  startDate?: string
  endDate?: string
  limit?: number
  productId?: string
}

const ProductPopular: React.FC<ProductPopularProps> = ({
  startDate,
  endDate,
  limit = 10,
  productId,
}) => {
  // If productId exists, start collapsed, otherwise start expanded
  const [expanded, setExpanded] = useState(!productId)
  const chartRef = useRef<HTMLDivElement>(null)
  const dataRef = useRef<ProductPopularData[] | undefined>(undefined)
  const handleProductClickRef = useRef<((productId: string) => void) | null>(
    null,
  )

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const { data, isLoading } = useAxios<ProductPopularData[]>({
    method: 'GET',
    url: '/order/products/popular',
    params: {
      startDate,
      endDate,
      limit,
    },
  })

  // Update refs when data or handler changes
  dataRef.current = data

  const handleProductClick = useCallback((productId: string) => {
    const currentQuery = queryString.parse(window.location.search)
    const newQuery = queryString.stringify({
      ...currentQuery,
      productId,
    })
    window.location.href = `${PageUrls.Report.Custormer}?${newQuery}`
  }, [])

  handleProductClickRef.current = handleProductClick

  // Update expanded state when productId changes
  useEffect(() => {
    setExpanded(!productId)
  }, [productId])

  // Add click event listener to yaxis labels as fallback
  // Must be called before early returns to follow React Hooks rules
  // Note: Main click handler is in chart.events.mounted
  useEffect(() => {
    if (!data || data.length === 0 || isLoading) return

    const chartContainer = chartRef.current
    if (!chartContainer) return

    // Fallback: Add click listener to container for yaxis labels
    const handleContainerClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | SVGElement

      // Check if clicked on yaxis label area
      if (target) {
        const yAxisLabel =
          target.closest('.apexcharts-yaxis-label') ||
          (target.tagName === 'text' &&
            target.closest('g.apexcharts-yaxis-label'))

        if (yAxisLabel) {
          const labelText =
            (yAxisLabel as HTMLElement).textContent?.trim() || ''
          const productIndex = data.findIndex((item) => {
            const fullName = item.productName
            const truncatedName =
              fullName.length > 30
                ? fullName.substring(0, 30) + '...'
                : fullName
            return (
              labelText === fullName ||
              labelText === truncatedName ||
              fullName.includes(labelText) ||
              labelText.includes(fullName)
            )
          })

          if (productIndex !== -1) {
            event.preventDefault()
            event.stopPropagation()
            handleProductClick(data[productIndex]._id)
          }
        }
      }
    }

    chartContainer.addEventListener('click', handleContainerClick, true)

    return () => {
      chartContainer.removeEventListener('click', handleContainerClick, true)
    }
  }, [data, isLoading, handleProductClick])

  if (isLoading) {
    return (
      <Card
        sx={{
          padding: 2,
          marginBottom: 4,
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={400}
        >
          <CircularProgress />
        </Box>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card
        sx={{
          padding: 2,
          marginBottom: 4,
        }}
      >
        <CardHeader title="สินค้าขายดี" />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <Typography variant="body1" color="text.secondary">
            ไม่มีข้อมูลสินค้าขายดีในช่วงเวลานี้
          </Typography>
        </Box>
      </Card>
    )
  }

  const chartData = {
    categories: data.map((item) => item.productName),
    quantities: data.map((item) => item.quantity),
    totals: data.map((item) => item.total),
    counts: data.map((item) => item.count),
  }

  const chartOptions = {
    chart: {
      id: 'product-popular-chart',
      toolbar: {
        show: true,
      },
      animations: {
        enabled: true,
        easing: 'easeinout' as const,
        speed: 800,
      },
      events: {
        mounted: (chartContext: any, config: any) => {
          // Wait a bit for chart to fully render
          setTimeout(() => {
            const chartContainer = chartRef.current
            const currentData = dataRef.current
            const clickHandler = handleProductClickRef.current

            if (
              !chartContainer ||
              !currentData ||
              currentData.length === 0 ||
              !clickHandler
            )
              return

            // Add click handlers to yaxis labels
            const yAxisLabels = chartContainer.querySelectorAll(
              '.apexcharts-yaxis-label text, .apexcharts-yaxis-label tspan, .apexcharts-yaxis-label',
            )

            yAxisLabels.forEach((label) => {
              const labelText =
                (label as HTMLElement | SVGElement).textContent?.trim() || ''
              const productIndex = currentData.findIndex((item) => {
                const fullName = item.productName
                const truncatedName =
                  fullName.length > 30
                    ? fullName.substring(0, 30) + '...'
                    : fullName
                return (
                  labelText === fullName ||
                  labelText === truncatedName ||
                  fullName.includes(labelText) ||
                  labelText.includes(fullName)
                )
              })

              if (productIndex !== -1) {
                const clickListener = (e: Event) => {
                  e.preventDefault()
                  e.stopPropagation()
                  clickHandler(currentData[productIndex]._id)
                }

                label.addEventListener('click', clickListener, true)

                // Add cursor pointer
                if (
                  label instanceof HTMLElement ||
                  label instanceof SVGElement
                ) {
                  label.style.cursor = 'pointer'
                }
              }
            })
          }, 500)
        },
        dataPointSelection: (event: any, chartContext: any, config: any) => {
          const dataPointIndex = config.dataPointIndex
          if (data && data[dataPointIndex]) {
            handleProductClick(data[dataPointIndex]._id)
          }
        },
        click: (event: any, chartContext: any, config: any) => {
          // Handle click on bar
          const dataPointIndex = config.dataPointIndex
          if (data && dataPointIndex !== undefined && data[dataPointIndex]) {
            handleProductClick(data[dataPointIndex]._id)
          }
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => numberFormat(val),
      style: {
        fontSize: '12px',
        fontWeight: 600,
        colors: ['#fff'],
      },
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          fontSize: '12px',
        },
        formatter: (value: string) => {
          // Truncate long product names
          return value.length > 30 ? value.substring(0, 30) + '...' : value
        },
      },
      title: {
        text: 'จำนวน (ชิ้น)',
        style: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '12px',
          cursor: 'pointer',
        },
      },
    },
    tooltip: {
      custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
        const product = data[dataPointIndex]
        return `
          <div style="padding: 10px; background: #fff; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
            <div style="font-weight: 600; margin-bottom: 8px; font-size: 14px;">${
              product.productName
            }</div>
            <div style="margin-bottom: 4px;">จำนวน: <strong>${numberFormat(
              product.quantity,
            )}</strong> ชิ้น</div>
            <div style="margin-bottom: 4px;">ยอดขาย: <strong>${numberFormat(
              product.total,
            )}</strong> บาท</div>
            <div style="margin-bottom: 4px;">จำนวนคำสั่งซื้อ: <strong>${numberFormat(
              product.count,
            )}</strong> รายการ</div>
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e0e0e0; font-size: 11px; color: #666;">
              💡 คลิกเพื่อกรองข้อมูลตามสินค้านี้
            </div>
          </div>
        `
      },
    },
    colors: ['#1976d2'],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'horizontal',
        shadeIntensity: 0.5,
        gradientToColors: ['#42a5f5'],
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 1,
        stops: [0, 100],
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
  }

  const chartSeries = [
    {
      name: 'จำนวนสินค้า (ชิ้น)',
      data: chartData.quantities,
    },
  ]

  return (
    <Card
      sx={{
        padding: 2,
        marginBottom: 4,
      }}
    >
      <CardHeader
        title="สินค้าขายดี"
        subheader={`แสดงสินค้าขายดี ${data.length} รายการ`}
        action={
          <IconButton
            onClick={handleExpandClick}
            aria-label="expand"
            sx={{
              transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.3s',
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            sx={{
              width: '100%',
              padding: 2,
              '& .apexcharts-yaxis-label': {
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.7,
                },
              },
            }}
            ref={chartRef}
          >
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type="bar"
              height={Math.max(400, data.length * 50)}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              padding: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 1 }}>
              สรุปข้อมูล
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    ยอดขายรวม
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {numberFormat(
                      data.reduce((sum, item) => sum + item.total, 0),
                    )}{' '}
                    บาท
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    จำนวนสินค้ารวม
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {numberFormat(
                      data.reduce((sum, item) => sum + item.quantity, 0),
                    )}{' '}
                    ชิ้น
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    จำนวนคำสั่งซื้อรวม
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {numberFormat(
                      data.reduce((sum, item) => sum + item.count, 0),
                    )}{' '}
                    รายการ
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      </Collapse>
    </Card>
  )
}

export default ProductPopular
